import React, { useState } from "react";
import { Bell, Download } from "lucide-react";
import { toast } from "sonner";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { usePushNotifications } from "../../hooks/usePushNotifications";

// Persistent customer prompts shown at the top of every customer page:
//  1. "Install app"  — stays until the PWA is installed (Android/Chromium browser).
//  2. "Turn on notifications" badge — stays until push permission is granted.
// Both are derived from live browser state, so they reappear on every login
// until the customer completes the action (no dismiss-and-forget). Hidden inside
// the native webview wrapper, which handles install/push natively.
export function CustomerAppPrompts() {
  const inNativeShell = typeof window !== "undefined" && !!window.ReactNativeWebView;
  const { installable, promptInstall } = usePwaInstall();
  const { supported, permission, subscribe } = usePushNotifications();
  const [busy, setBusy] = useState(false);

  const showInstall = !inNativeShell && installable;
  const showNotify = !inNativeShell && supported && permission !== "granted";

  if (!showInstall && !showNotify) return null;

  const onInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") toast.success("Installing the app…");
  };

  const onNotify = async () => {
    setBusy(true);
    try {
      await subscribe();
      toast.success("Notifications turned on — you'll get delivery alerts.");
    } catch (e) {
      toast.error("Couldn't turn on notifications. Please allow them in your browser settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-3 flex max-w-md flex-col gap-2 px-4 md:max-w-xl lg:max-w-2xl" data-testid="customer-app-prompts">
      {showInstall && (
        <button
          type="button"
          onClick={onInstall}
          data-testid="customer-install-app-button"
          className="flex w-full items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left transition-colors active:scale-[0.99]"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white">
            <Download size={18} strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-main">Install The Virsa Tiffin app</span>
            <span className="block text-xs text-text-muted">Add it to your home screen for one-tap access.</span>
          </span>
          <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white">Install</span>
        </button>
      )}

      {showNotify && (
        <button
          type="button"
          onClick={onNotify}
          disabled={busy}
          data-testid="customer-enable-notifications-badge"
          className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left transition-colors active:scale-[0.99] disabled:opacity-60"
        >
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-text-main/90 text-white">
            <Bell size={18} strokeWidth={2} />
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface bg-danger" data-testid="customer-notifications-dot" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-main">Turn on notifications</span>
            <span className="block text-xs text-text-muted">Get alerted the moment your tiffin is on the way.</span>
          </span>
          <span className="shrink-0 rounded-full bg-text-main px-3 py-1.5 text-xs font-semibold text-white">
            {busy ? "…" : "Turn on"}
          </span>
        </button>
      )}
    </div>
  );
}
